"use client"

import { useSyncExternalStore } from "react"

// A video playhead kept outside React state. It changes several times a second during
// playback; putting it in a big component's useState re-rendered the whole component each
// time (see "High-frequency state" in CLAUDE.md). Only the leaf components below subscribe.
export interface PlayheadStore {
  get: () => number
  set: (t: number) => void
  subscribe: (cb: () => void) => () => void
}

export function createPlayheadStore(): PlayheadStore {
  let value = 0
  const listeners = new Set<() => void>()
  return {
    get: () => value,
    set: (t) => {
      if (t === value) return
      value = t
      listeners.forEach((l) => l())
    },
    subscribe: (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
  }
}

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00"
  if (seconds < 1) return "< 1s"
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  return h > 0
    ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
    : `${m}:${s.toString().padStart(2, "0")}`
}

export function usePlayhead(store: PlayheadStore) {
  return useSyncExternalStore(store.subscribe, store.get, () => 0)
}

export function PlayheadReadout({ store, duration }: { store: PlayheadStore; duration: number }) {
  const t = usePlayhead(store)
  return (
    <>
      {formatTime(t)} / {formatTime(duration)}
    </>
  )
}

export function PlayheadSeek({
  store,
  duration,
  ariaLabel,
  onSeek,
}: {
  store: PlayheadStore
  duration: number
  ariaLabel: string
  onSeek: (t: number) => void
}) {
  const t = usePlayhead(store)
  const ok = Number.isFinite(duration) && duration > 0
  return (
    <input
      type="range"
      min="0"
      max={ok ? duration : 0}
      step="0.1"
      value={ok ? Math.min(t, duration) : 0}
      disabled={!ok}
      onChange={(e) => {
        const next = Number.parseFloat(e.target.value)
        if (Number.isFinite(next)) onSeek(next)
      }}
      className="w-full h-1.5"
      aria-label={ariaLabel}
    />
  )
}

/** Renders children with the current playhead, subscribing on its own so the parent component never re-renders
 * when the playhead moves. Use it to wrap a child that needs the time every frame (an overlay, a timeline). */
export function WithPlayhead({ store, children }: { store: PlayheadStore; children: (t: number) => React.ReactNode }) {
  const t = usePlayhead(store)
  return <>{children(t)}</>
}
